import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import Radio from 'component/Layout/Form/Radio';

import classNames from 'classnames';

import { useHotelCategory } from 'hooks/form/filter';
import { useAccommodationMeta } from 'hooks/form/meta';
import { useChangedFilterListForSection } from 'hooks/form/useChangedFilterList';
import { QaIdentifier } from 'hooks/useQa';

import {
    buildCategoryLogicUrlValue,
    buildCategoryMinLogicMetaKey,
    getHotelCategoryMinLogicValue,
    isCategorySelected,
} from 'utility/Format/category';

import { HotelCategoryFilter } from 'types/Search/filter';

import styles from '../Filter.module.scss';
import Segment from '../Segment';
import {
    MetaValue,
    useDisabledStatus,
} from '../Segment/__hooks/useDisabledStatus';
import FilterLabel from '../Segment/FilterLabel';
import { FilterElement } from '../Segment/types';
import { SectionName } from '../types';

import { getHotelCategoryFilterLabel } from './__helper/hotelCategoryFilterLabel';

const hotelCategoryFilterList = [
    HotelCategoryFilter.Five,
    HotelCategoryFilter.Four,
    HotelCategoryFilter.Three,
    HotelCategoryFilter.Two,
    HotelCategoryFilter.One,
];
const HotelCategory = ({ disableWhen = MetaValue.NoResult }) => {
    const { t } = useTranslation();
    const [hotelCategoryMinLogicValue, setHotelCategoryMinLogicValue] =
        useHotelCategory();
    const [accommodationMeta] = useAccommodationMeta();
    const getDisabledStatus = useDisabledStatus(disableWhen);
    const { onClear, onSelect, isFilterChanged } =
        useChangedFilterListForSection(SectionName.HotelCategory, () => {
            const currentValue = hotelCategoryFilterList.find((category) =>
                isCategorySelected(category, hotelCategoryMinLogicValue)
            );

            return !!currentValue
                ? `${SectionName.HotelCategory}${currentValue}`
                : undefined;
        });

    const onSelect_ = useCallback(
        (selectedHotelCategory: HotelCategoryFilter) => {
            onSelect(`${SectionName.HotelCategory}${selectedHotelCategory}`);
            setHotelCategoryMinLogicValue(
                getHotelCategoryMinLogicValue(selectedHotelCategory)
            );
        },
        [setHotelCategoryMinLogicValue, onSelect]
    );
    const filterList: FilterElement[] = hotelCategoryFilterList.map(
        (currentHotelCategory, index) => {
            const isChecked = isCategorySelected(
                currentHotelCategory,
                hotelCategoryMinLogicValue
            );
            const label = getHotelCategoryFilterLabel(currentHotelCategory, t);
            const meta =
                accommodationMeta?.hotelCategory[
                    buildCategoryMinLogicMetaKey(currentHotelCategory)
                ] ?? undefined;

            const isDisabled = !isChecked && meta && getDisabledStatus(meta);

            return {
                content: (
                    <Radio
                        key={currentHotelCategory}
                        input={{
                            id: `cbHotelCategory${index}`,
                        }}
                        isChecked={isChecked}
                        onChange={() => onSelect_(currentHotelCategory)}
                        withUXSelection
                        isDisabled={isDisabled}
                        isOnlyDeselectable={
                            meta?.hasCount && meta?.count === 0 && isChecked
                        }
                        customClass={{
                            Wrapper: classNames({
                                [styles.Changed]: isFilterChanged(
                                    `${SectionName.HotelCategory}${currentHotelCategory}`
                                ),
                            }),
                        }}
                    >
                        <FilterLabel
                            labelText={label}
                            {...accommodationMeta?.hotelCategory[
                                buildCategoryMinLogicMetaKey(
                                    currentHotelCategory
                                )
                            ]}
                        />
                    </Radio>
                ),
                isSelected: isChecked,
                filterValue: label,
                kiFilterData: {
                    label,
                    isSelectable: !isDisabled,
                    urlKey: SectionName.HotelCategory,
                    value:
                        buildCategoryLogicUrlValue(currentHotelCategory) ?? '',
                    ...meta,
                },
            };
        }
    );

    const headline = t('TRANS-hotel-category');

    return (
        <Segment
            headline={{ text: headline }}
            withDeleteLink
            qaIdentifier={QaIdentifier.Search_Filter_Section_HotelCategory}
            onDelete={() => {
                onClear();
                setHotelCategoryMinLogicValue(new Set());
            }}
            filterList={filterList}
            kiFilterGroupData={{
                refKey: SectionName.HotelCategory,
                label: headline,
                multi: false,
                minLogic: true,
            }}
        />
    );
};

export default HotelCategory;
